"use client";
import type { Media, OmniboxDemo } from "@mw/shared/payload-types";
import type React from "react";

import { Image } from "@components/core/Image";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import Convai20 from "@ui/icons/20x20/convai-20.svg";
import Dubbing20 from "@ui/icons/20x20/dubbing-20.svg";
import ElevenMusic20 from "@ui/icons/20x20/eleven-music-20.svg";
import Reader20 from "@ui/icons/20x20/reader-20.svg";
import Sts20 from "@ui/icons/20x20/sts-20.svg";
import Stt20 from "@ui/icons/20x20/stt-20.svg";
import Tts20 from "@ui/icons/20x20/tts-20.svg";
import Ttsfx20 from "@ui/icons/20x20/ttsfx-20.svg";
import VoiceCloning20 from "@ui/icons/20x20/voice-cloning-20.svg";
import { cn } from "@ui/lib/utils";
import { runAfterNextBrowserPaint } from "@utilities/afterNextPaint";
import * as m from "motion/react-m";
import { useEffect, useRef, useState } from "react";

const SUPPORTED_LOCAL_ICONS: Partial<Record<OmniboxDemo["component"], () => React.ReactNode>> = {
  tts: () => <Tts20 />,
  sts: () => <Sts20 />,
  dubbing: () => <Dubbing20 />,
  ttsfx: () => <Ttsfx20 />,
  voiceCloning: () => <VoiceCloning20 />,
  stt: () => <Stt20 />,
  convAI: () => <Convai20 />,
  elevenReader: () => <Reader20 />,
  music: () => <ElevenMusic20 />,
};

// Reusable components
const ScrollOverlay = ({ direction, isVisible }: { direction: "left" | "right"; isVisible: boolean }) => (
  <div
    className={cn(
      "effect-opacity pointer-events-none absolute top-0 h-full w-44 transition-opacity duration-200",
      direction === "left"
        ? "left-0 bg-gradient-to-r from-[white] to-transparent"
        : "right-0 bg-gradient-to-l from-[white] to-transparent",
      isVisible ? "opacity-100" : "opacity-0",
    )}
  />
);

const ScrollButton = ({
  direction,
  isVisible,
  onClick,
}: {
  direction: "left" | "right";
  isVisible: boolean;
  onClick: () => void;
}) => (
  <button
    aria-label={`Scroll ${direction}`}
    className={cn(
      "effect-opacity absolute top-1/2 z-10 flex h-32 w-32 -translate-y-1/2 items-center justify-center rounded-full border border-default bg-default text-default shadow-sm transition-all hover:bg-darker",
      direction === "left" ? "left-4" : "right-4",
      isVisible ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
    )}
    onClick={onClick}
  >
    {direction === "left" ? <ChevronLeft16 /> : <ChevronRight16 />}
  </button>
);

interface ScrollableTabsProps {
  activeTab: OmniboxDemo["component"];
  demos: Array<{
    component: OmniboxDemo["component"];
    icon?: Media | null | number | string | undefined;
    title: string;
  }>;
  onTabSelect: (index: number, component: OmniboxDemo["component"]) => void;
}

export function ScrollableTabs({ activeTab, demos, onTabSelect }: ScrollableTabsProps) {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const buttonRefs = useRef<HTMLButtonElement[]>([]);
  const tabsListRef = useRef<HTMLDivElement>(null);

  const handleScroll = (direction: "left" | "right") => {
    if (tabsListRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      tabsListRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const checkOverflow = () => {
    if (tabsListRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = tabsListRef.current;
      const hasOverflowContent = scrollWidth > clientWidth;

      setHasOverflow(hasOverflowContent);
      if (hasOverflowContent) {
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding issues
      } else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    }
  };

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    const checkOverflowAfterRender = () => {
      requestAnimationFrame(() => {
        checkOverflow();
      });
    };

    checkOverflowAfterRender();

    // Check overflow on window resize
    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [demos]);

  useEffect(() => {
    const scrollContainer = tabsListRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkOverflow);
      return () => scrollContainer.removeEventListener("scroll", checkOverflow);
    }
  }, []);

  return (
    <div className='relative mb-16 flex justify-center'>
      <div className='scrollbar-none flex gap-8 overflow-x-auto' ref={tabsListRef}>
        {demos.map(({ component, icon, title }, index) => {
          const active = activeTab === component;
          return (
            <button
              className={cn(
                "effect-color effect-focus-inverse f-ui-03 inline-flex h-40 w-fit shrink-0 items-center justify-center whitespace-nowrap rounded-full border bg-default p-0 text-default ring-offset-darkest disabled:cursor-not-allowed data-[state=active]:border-darkest",
                "rounded-lg text-light enabled:hover:border-darkest",
                active && "bg-dark text-default",
              )}
              disabled={active}
              key={component}
              onClick={() => {
                onTabSelect(index, component);
                // Center the clicked tab
                runAfterNextBrowserPaint(() => {
                  buttonRefs.current[index]?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "center",
                  });
                });
              }}
              ref={el => {
                buttonRefs.current[index] = el!;
              }}
            >
              <span
                className={cn(
                  "effect-opacity flex h-40 w-40 shrink-0 items-center justify-center",
                  active ? "opacity-100" : "opacity-50",
                )}
              >
                {SUPPORTED_LOCAL_ICONS[component] ? (
                  SUPPORTED_LOCAL_ICONS[component]!()
                ) : (
                  <Image className='h-20 w-20' height={20} resource={icon} width={20} />
                )}
              </span>
              <m.span className='pe-12'>{title}</m.span>
            </button>
          );
        })}
        {/* Spacer to ensure overlay is always visible */}
        {hasOverflow && <div className='w-44 shrink-0' />}
      </div>

      {/* Gradient overlay on the left - only when scrolled right */}
      <ScrollOverlay direction='left' isVisible={canScrollLeft} />

      {/* Gradient overlay on the right - only when can scroll right */}
      <ScrollOverlay direction='right' isVisible />

      {/* Left scroll button - only when can scroll left */}
      <ScrollButton direction='left' isVisible={canScrollLeft} onClick={() => handleScroll("left")} />

      {/* Right scroll button - only when can scroll right */}
      <ScrollButton direction='right' isVisible={canScrollRight} onClick={() => handleScroll("right")} />
    </div>
  );
}
